import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { ContentLog } from "../types/dashboard";
import useBrandHistoryApi from "../api/useBrandHistoryApi";
import { BRAND_HISTORY_REFRESH_EVENT } from "../utils/brand-history-events";
import linkedinIcon from "../assets/images/social/linkedin.svg";
import xIcon from "../assets/images/social/x-twitter.svg";
import instagramIcon from "../assets/images/social/instagram.svg";
import facebookIcon from "../assets/images/social/facebook.svg";
import threadsIcon from "../assets/images/social/threads.svg";
import redditIcon from "../assets/images/social/reddit.svg";

const HISTORY_PAGE_SIZE = 15;
const HISTORY_FETCH_LIMIT = 500;

const normalizePlatformId = (platform: string): string => {
  const normalized = platform.toLowerCase();
  if (normalized === "linkedin") return "linkedin";
  if (normalized === "x") return "x";
  return normalized;
};

const resolvePlatform = (platform: unknown): string => {
  if (typeof platform === "string") return platform;
  if (
    platform &&
    typeof platform === "object" &&
    "platform" in platform &&
    typeof (platform as { platform?: unknown }).platform === "string"
  ) {
    return (platform as { platform: string }).platform;
  }
  return "";
};

const dedupeMedia = (media: { type: string; url: string }[] = []) => {
  const seen = new Set<string>();
  return media.filter((item) => {
    const key = `${item.type}:${item.url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getCombinedStatus = (items: ContentLog[]): string => {
  const statuses = Array.from(
    new Set(items.map((item) => (item.status || "").toUpperCase()).filter(Boolean))
  );

  if (statuses.length === 0) return "";
  if (statuses.length === 1) return statuses[0];
  if (statuses.includes("PARTIAL_FAILURE")) return "PARTIAL_FAILURE";
  if (statuses.includes("FAILED") && statuses.some((status) => status !== "FAILED")) {
    return "PARTIAL_FAILURE";
  }
  if (statuses.includes("PROCESSING")) return "PROCESSING";
  if (statuses.includes("SCHEDULED") && statuses.includes("PUBLISHED")) {
    return "PROCESSING";
  }
  if (statuses.every((status) => status === "SCHEDULED")) return "SCHEDULED";
  return statuses[0];
};

const getCombinedFailureReason = (items: ContentLog[]): string | null => {
  const reasons = Array.from(
    new Set(
      items
        .map((item) => item.content?.pfm_result?.failure_reason?.trim())
        .filter((reason): reason is string => Boolean(reason))
    )
  );

  if (reasons.length === 0) return null;
  return reasons.join(" | ");
};

const useBrandHistory = (brandId: string) => {
  const [history, setHistory] = useState<ContentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReconciling, setIsReconciling] = useState(false);
  const [page, setPage] = useState(1);
  const prevBrandIdRef = useRef<string>("");

  const { getBrandHistory } = useBrandHistoryApi();

  const getHistory = useCallback(
    async (brandId: string, reconcile: boolean = false) => {
      setLoading(true);
      if (reconcile) {
        setIsReconciling(true);
      }
      try {
        const data = await getBrandHistory(
          brandId,
          0,
          HISTORY_FETCH_LIMIT,
          reconcile
        );
        if (data) {
          setHistory(data.list);
        }
      } finally {
        setLoading(false);
        if (reconcile) {
          setIsReconciling(false);
        }
      }
    },
    [getBrandHistory]
  );

  useEffect(() => {
    if (!brandId) return;
    const prevBrand = prevBrandIdRef.current;
    if (prevBrand !== brandId) {
      prevBrandIdRef.current = brandId;
      setPage(1);
      getHistory(brandId, false);
    }
  }, [brandId, getHistory]);

  useEffect(() => {
    if (typeof window === "undefined" || !brandId) {
      return;
    }

    const handleRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ brandId?: string }>).detail;
      if (detail?.brandId !== brandId) {
        return;
      }

      setPage(1);
      getHistory(brandId, false);
    };

    window.addEventListener(BRAND_HISTORY_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(BRAND_HISTORY_REFRESH_EVENT, handleRefresh);
    };
  }, [brandId, getHistory]);

  const refreshHistory = useCallback(() => {
    if (brandId) {
      setPage(1);
      getHistory(brandId, true);
    }
  }, [brandId, getHistory]);

  const goToPrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const getIconForPlatform = useCallback((platform: string): string | null => {
    switch (platform) {
      case "linkedIn":
      case "linkedin":
        return linkedinIcon;
      case "twitter":
      case "x":
        return xIcon;
      case "instagram":
        return instagramIcon;
      case "facebook":
        return facebookIcon;
      case "threads":
        return threadsIcon;
      case "reddit":
        return redditIcon;
      default:
        return null;
    }
  }, []);

  const getLogPlatforms = useCallback((log: ContentLog): string[] => {
    const rawPlatforms = log.content?.platforms ?? [];
    const resolvedPlatforms = rawPlatforms
      .map(resolvePlatform)
      .filter(Boolean)
      .map(normalizePlatformId);

    if (resolvedPlatforms.length > 0) {
      return Array.from(new Set(resolvedPlatforms));
    }

    return log.platform ? [normalizePlatformId(log.platform)] : [];
  }, []);

  const groupedHistory = useMemo(() => {
    const groupedLogs = new Map<string, ContentLog[]>();

    history.forEach((log) => {
      const key = log.post_id || log.id;
      const items = groupedLogs.get(key) ?? [];
      items.push(log);
      groupedLogs.set(key, items);
    });

    return Array.from(groupedLogs.values()).map((items) => {
      if (items.length === 1) {
        const [singleItem] = items;
        const failureReason = singleItem.content?.pfm_result?.failure_reason ?? null;
        return {
          ...singleItem,
          content: {
            ...singleItem.content,
            platforms: getLogPlatforms(singleItem),
            pfm_result: singleItem.content?.pfm_result
              ? {
                  ...singleItem.content.pfm_result,
                  overall_status:
                    singleItem.content.pfm_result.overall_status ?? singleItem.status,
                  failure_reason: failureReason,
                }
              : undefined,
          },
        };
      }

      const baseItem = items[0];
      const combinedStatus = getCombinedStatus(items);
      const combinedFailureReason = getCombinedFailureReason(items);
      const platforms = Array.from(
        new Set(items.flatMap((item) => getLogPlatforms(item)))
      );

      const platformCaptions = items.reduce<Record<string, string>>(
        (acc, item) => {
          const itemPlatforms = getLogPlatforms(item);
          const existingCaptions = item.content?.platformCaptions ?? {};

          itemPlatforms.forEach((platform) => {
            const caption =
              existingCaptions[platform] ??
              existingCaptions[normalizePlatformId(platform)] ??
              item.content?.content ??
              "";

            if (caption) {
              acc[platform] = caption;
            }
          });

          return acc;
        },
        {}
      );

      const platformMedia = items.reduce<
        Record<string, { type: string; url: string }[]>
      >((acc, item) => {
        const itemPlatforms = getLogPlatforms(item);
        const existingPlatformMedia = item.content?.platformMedia ?? {};

        itemPlatforms.forEach((platform) => {
          const mediaForPlatform = dedupeMedia(
            existingPlatformMedia[platform] ?? item.content?.media ?? []
          );
          if (mediaForPlatform.length > 0) {
            acc[platform] = mediaForPlatform;
          }
        });

        return acc;
      }, {});

      const mediaSignatures = Object.values(platformMedia).map((media) =>
        media.map((item) => `${item.type}:${item.url}`).join("|")
      );
      const uniqueMediaSignatures = new Set(
        mediaSignatures.filter((signature) => signature.length > 0)
      );
      const useCommonMediaForAllPlatforms = uniqueMediaSignatures.size <= 1;
      const commonMedia = useCommonMediaForAllPlatforms
        ? dedupeMedia(
            Object.values(platformMedia)[0] ?? baseItem.content?.media ?? []
          )
        : dedupeMedia(baseItem.content?.media ?? []);

      return {
        ...baseItem,
        status: combinedStatus,
        content: {
          ...baseItem.content,
          content:
            platformCaptions[platforms[0]] ??
            baseItem.content?.content ??
            "",
          platforms,
          platformCaptions,
          media: commonMedia,
          platformMedia:
            useCommonMediaForAllPlatforms || Object.keys(platformMedia).length === 0
              ? undefined
              : platformMedia,
          useCommonMediaForAllPlatforms,
          pfm_result:
            baseItem.content?.pfm_result || combinedFailureReason
              ? {
                  ...(baseItem.content?.pfm_result ?? {}),
                  status: combinedStatus,
                  overall_status: combinedStatus,
                  failure_reason: combinedFailureReason,
                }
              : undefined,
        },
      };
    });
  }, [getLogPlatforms, history]);

  const totalLogs = groupedHistory.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalLogs / HISTORY_PAGE_SIZE)),
    [totalLogs]
  );

  const paginatedHistory = useMemo(() => {
    const start = (page - 1) * HISTORY_PAGE_SIZE;
    return groupedHistory.slice(start, start + HISTORY_PAGE_SIZE);
  }, [groupedHistory, page]);

  const goToNextPage = useCallback(() => {
    setPage((p) => Math.min(totalPages, p + 1));
  }, [totalPages]);

  return {
    history: paginatedHistory,
    fullHistory: groupedHistory,
    refreshHistory,
    loading,
    isReconciling,
    page,
    totalPages,
    totalLogs,
    goToPrevPage,
    goToNextPage,
    pageSize: HISTORY_PAGE_SIZE,
    getIconForPlatform,
  };
};

export default useBrandHistory;
